<script setup>
import CompanyLogo from '@/components/CompanyLogo.vue';
import KHKNotificationCard from '@/components/ui/KHKNotificationCard.vue';
import { useSelectedBrand } from '@/composables/useSelectedBrand';
import { Button, Select } from 'primevue';
import { onMounted, ref } from 'vue';

// Brand picker (#2122): a login-style page reached from the user popover. The
// user chooses a brand and Continue persists it (PUT /admin/me/brand) then does a
// full load into the dashboard so every view re-fetches under the new brand.
const { selectedBrand, brands, loadingBrands, switching, loadBrands, switchBrand } = useSelectedBrand();

const chosenCode = ref(null);
const errorMessage = ref('');

onMounted(async () => {
    await loadBrands();
    // Default the dropdown to the brand currently in effect.
    chosenCode.value = selectedBrand.value.code;
});

async function handleContinue() {
    errorMessage.value = '';
    try {
        // Redirect to the dashboard root so the app re-hydrates under the brand.
        await switchBrand(chosenCode.value, '/');
    } catch (error) {
        console.error('Failed to switch brand', error);
        errorMessage.value = error?.message || 'Failed to switch brand. Please try again.';
    }
}
</script>

<template>
    <div class="auth-screen flex items-center justify-center min-h-screen min-w-[100vw] overflow-hidden bg-surface-ground">
        <div class="flex flex-col items-center justify-center">
            <div
                style="border-radius: 56px; padding: 0.3rem; background: linear-gradient(180deg, var(--primary-color) 10%, rgba(33, 150, 243, 0) 30%)">
                <div class="w-full bg-surface-card py-20 px-8 sm:px-20" style="border-radius: 53px">
                    <div class="text-center mb-8 flex flex-col justify-center items-center gap-3">
                        <div class="auth-logo">
                            <CompanyLogo />
                        </div>
                        <div class="auth-subtitle">
                            Select a brand
                        </div>
                    </div>

                    <div class="w-full max-w-[30rem] flex flex-col items-center gap-5">
                        <KHKNotificationCard v-if="errorMessage" :notification="{ message: errorMessage }" type="error"
                            class="w-full" />

                        <div class="brand-choice w-full">
                            <label class="brand-label">
                                <i class="pi pi-building"></i>
                                <span>Brand</span>
                            </label>
                            <Select v-model="chosenCode" :options="brands" optionLabel="name" optionValue="code"
                                :loading="loadingBrands || switching" :disabled="loadingBrands || switching"
                                :placeholder="loadingBrands ? 'Loading brands...' : 'Choose a brand'"
                                class="w-full auth-select" overlayClass="auth-select-panel" />
                        </div>

                        <Button @click="handleContinue" :loading="switching"
                            :disabled="loadingBrands || switching || !chosenCode" label="Continue"
                            icon="pi pi-arrow-right" iconPos="right" class="auth-button w-full" />
                    </div>
                </div>
            </div>

            <p class="auth-footer">
                © {{ new Date().getFullYear() }} KHK Games. All rights reserved.
            </p>
        </div>
    </div>
</template>
